import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { avatarFor } from "@/data/demo";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const RISK_TONE = { Aggressive: "#EF4444", Moderate: "#F59E0B", Conservative: "#10B981" };
const SIP_TONE = { Active: "#10B981", "At Risk": "#EF4444", Stopped: "#9CA3AF" };
const NOW_YEAR = 2026;

const vintageYears = (c) => (c.since ? NOW_YEAR - c.since : null);
const ageBand = (a) => (a == null ? "Unknown" : a < 30 ? "Under 30" : a < 40 ? "30–39" : a < 50 ? "40–49" : a < 60 ? "50–59" : "60+");
const vintageBand = (v) => (v == null ? "Unknown" : v < 2 ? "< 2 yrs" : v < 5 ? "2–4 yrs" : v < 8 ? "5–7 yrs" : "8+ yrs");

const PALETTE = ["#3B82F6", "#8B5CF6", "#F97316", "#10B981", "#06B6D4", "#F43F5E", "#A16207"];

function tally(items, keyFn) {
  const m = {};
  items.forEach((c) => { const k = keyFn(c); if (k != null) m[k] = (m[k] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
}

function DistPanel({ title, icon, color, rows, total }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4" data-testid={`demo-${title.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3"><Icon name={icon} size={15} style={{ color }} /><span className="font-semibold text-sm text-gray-700">{title}</span></div>
      <div className="space-y-2">
        {rows.map(([label, count], i) => (
          <div key={label} className="flex items-center gap-2 text-xs">
            <span className="w-24 shrink-0 text-gray-600 truncate" title={label}>{label}</span>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${total ? (count / total) * 100 : 0}%`, background: PALETTE[i % PALETTE.length] }} /></div>
            <span className="w-6 text-right text-gray-400 tabular-nums">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Clients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => api.get("/clients").then((r) => setClients(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.city || "").toLowerCase().includes(q.toLowerCase())), [clients, q]);
  const totalAum = useMemo(() => clients.reduce((s, c) => s + (c.aum_val || 0), 0), [clients]);

  const demo = useMemo(() => ({
    total: clients.length,
    age: tally(clients, (c) => ageBand(c.age)),
    segment: tally(clients, (c) => c.segment || "Retail"),
    city: tally(clients, (c) => c.city).slice(0, 6),
    occupation: tally(clients, (c) => c.occupation).slice(0, 6),
    vintage: tally(clients, (c) => vintageBand(vintageYears(c))),
  }), [clients]);

  const openClient = async (id) => {
    try { const { data } = await api.get(`/clients/${id}`); setSel(data); }
    catch { toast.error("Could not load client"); }
  };
  const askAI = (c) => nav("/app/ai", { state: { prompt: `Give me a concise advisory summary and next-best-action for my client ${c.name} (AUM ${c.aum}, risk ${c.risk}, SIP ${c.sip_status}). Keep it practical.` } });

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="My Clients" subtitle="Client 360 — demographics, holdings, allocation, SIPs and AI insights." icon="UserSquare2" color="#3B82F6"
        action={<span className="text-sm text-gray-500">Book AUM <b className="text-gray-800">₹{totalAum} L</b> · {clients.length} clients</span>} />

      {/* Demographics overview */}
      {clients.length > 0 && (
        <Card className="mb-5" data-testid="clients-demographics">
          <div className="flex items-center gap-2 mb-4"><Icon name="Users" size={18} className="text-blue-600" /><h3 className="font-head font-semibold text-lg">Client demographics</h3><span className="text-xs text-gray-400 ml-auto">Across {clients.length} clients</span></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <DistPanel title="Age" icon="CalendarDays" color="#3B82F6" rows={demo.age} total={demo.total} />
            <DistPanel title="Segment" icon="Crown" color="#8B5CF6" rows={demo.segment} total={demo.total} />
            <DistPanel title="City" icon="MapPin" color="#F97316" rows={demo.city} total={demo.total} />
            <DistPanel title="Occupation" icon="Briefcase" color="#10B981" rows={demo.occupation} total={demo.total} />
            <DistPanel title="Vintage" icon="History" color="#06B6D4" rows={demo.vintage} total={demo.total} />
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 mb-5 max-w-md">
        <div className="flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
          <Icon name="Search" size={16} className="text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients by name or city…" data-testid="clients-search" className="flex-1 text-sm outline-none bg-transparent" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="clients-grid">
        {filtered.map((c) => {
          const v = vintageYears(c);
          return (
            <button key={c.id} onClick={() => openClient(c.id)} data-testid={`client-card-${c.id}`} className="text-left card-mfx p-5 hover-lift">
              <div className="flex items-center gap-3">
                <img src={avatarFor(c.name)} alt={c.name} className="w-11 h-11 rounded-full object-cover" />
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2">{c.name}{c.segment === "HNI" && <span className="text-[10px] font-bold bg-violet-100 text-violet-600 rounded-full px-1.5 py-0.5">HNI</span>}</div>
                  <div className="text-xs text-gray-400 flex items-center gap-1"><Icon name="MapPin" size={11} />{c.city}</div>
                </div>
                <span className="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: `${SIP_TONE[c.sip_status]}22`, color: SIP_TONE[c.sip_status] }}>{c.sip_status}</span>
              </div>
              {/* Demographic chips */}
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`client-demo-${c.id}`}>
                {c.age != null && <Chip icon="CalendarDays">{c.age} yrs</Chip>}
                {c.occupation && <Chip icon="Briefcase">{c.occupation}</Chip>}
                {v != null && <Chip icon="History">{v === 0 ? "New" : `${v} yr${v > 1 ? "s" : ""} client`}</Chip>}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><div className="font-head font-bold text-lg text-blue-600">{c.aum}</div><div className="text-[11px] text-gray-400">AUM</div></div>
                <div><div className="font-head font-bold text-lg">{c.folios}</div><div className="text-[11px] text-gray-400">Folios</div></div>
                <div><div className="font-head font-bold text-lg">{c.sips}</div><div className="text-[11px] text-gray-400">SIPs</div></div>
              </div>
              {c.ai_insights?.[0] && <div className="mt-3 text-xs text-gray-600 bg-violet-50 rounded-lg px-3 py-2 flex gap-1.5"><Icon name="Sparkles" size={13} className="text-violet-500 shrink-0 mt-0.5" />{c.ai_insights[0]}</div>}
            </button>
          );
        })}
      </div>

      {sel && <Client360 client={sel} onClose={() => setSel(null)} onAskAI={() => askAI(sel)} />}
    </div>
  );
}

function Chip({ icon, children }) {
  return <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5"><Icon name={icon} size={11} />{children}</span>;
}

function Client360({ client: c, onClose, onAskAI }) {
  const v = vintageYears(c);
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="client-360-modal">
      <div className="w-full max-w-2xl bg-[#FEF9F4] h-full overflow-y-auto no-scrollbar shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 glass px-6 py-4 flex items-center gap-3 border-b border-orange-100 z-10">
          <img src={avatarFor(c.name)} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
          <div><div className="font-head font-bold text-lg">{c.name}</div><div className="text-xs text-gray-400">{c.city} · {c.segment} · Risk: <span style={{ color: RISK_TONE[c.risk] }}>{c.risk}</span></div></div>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700" data-testid="client-360-close"><Icon name="X" size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Demographics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="client-360-demographics">
            {[["Age", c.age != null ? `${c.age} yrs` : "—", "CalendarDays", "#3B82F6"],
              ["Occupation", c.occupation || "—", "Briefcase", "#10B981"],
              ["Segment", c.segment || "Retail", "Crown", "#8B5CF6"],
              ["Client since", c.since ? `${c.since} · ${v} yr${v === 1 ? "" : "s"}` : "—", "History", "#06B6D4"]].map(([l, val, icon, col]) => (
              <div key={l} className="card-mfx p-4"><div className="flex items-center gap-1.5 text-[11px] text-gray-400"><Icon name={icon} size={12} style={{ color: col }} />{l}</div><div className="font-semibold text-sm mt-0.5">{val}</div></div>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["AUM", c.aum, "#3B82F6"], ["Folios", c.folios, "#8B5CF6"], ["SIPs", c.sips, "#F97316"], ["SIP/mo", c.sip_amount, "#10B981"]].map(([l, val, col]) => (
              <div key={l} className="card-mfx p-4"><div className="text-[11px] text-gray-400">{l}</div><div className="font-head font-bold text-lg" style={{ color: col }}>{val}</div></div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Btn onClick={onAskAI} data-testid="client-ask-ai"><Icon name="Sparkles" size={15} /> Ask AI about this client</Btn>
            <Btn variant="outline"><Icon name="Phone" size={15} /> Call</Btn>
            <Btn variant="outline"><Icon name="MessageCircle" size={15} /> WhatsApp</Btn>
            <Btn variant="outline"><Icon name="CalendarClock" size={15} /> Schedule</Btn>
            <Btn variant="outline"><Icon name="PieChart" size={15} /> Review Portfolio</Btn>
          </div>

          <Card>
            <h4 className="font-head font-semibold flex items-center gap-2 mb-3"><Icon name="Sparkles" size={16} className="text-violet-600" /> AI Client Insights</h4>
            <div className="space-y-2">{c.ai_insights?.map((t) => <div key={t} className="flex gap-2 text-sm text-gray-700 bg-violet-50 rounded-xl px-3 py-2"><Icon name="Lightbulb" size={15} className="text-violet-500 shrink-0 mt-0.5" />{t}</div>)}</div>
            <div className="mt-3 text-sm bg-orange-50 text-orange-800 rounded-xl px-3 py-2 flex gap-2"><Icon name="Target" size={15} className="shrink-0 mt-0.5" /><b>Next action:</b> {c.next_action}</div>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card>
              <h4 className="font-head font-semibold mb-2">Asset allocation</h4>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart><Pie data={c.category_alloc} dataKey="value" innerRadius={40} outerRadius={70} paddingAngle={3}>
                  {c.category_alloc?.map((x) => <Cell key={x.name} fill={x.color} />)}</Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-1 mt-1">{c.category_alloc?.map((x) => (
                <div key={x.name} className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full" style={{ background: x.color }} />{x.name}<span className="ml-auto text-gray-400">{x.value}%</span></div>
              ))}</div>
            </Card>
            <Card>
              <h4 className="font-head font-semibold mb-3">Profile</h4>
              <ul className="text-sm space-y-2 text-gray-600">
                <li className="flex justify-between"><span>Equity / Debt / Hybrid</span><b>{c.allocation?.equity}/{c.allocation?.debt}/{c.allocation?.hybrid}%</b></li>
                <li className="flex justify-between"><span>SIP status</span><span style={{ color: SIP_TONE[c.sip_status] }}>{c.sip_status}</span></li>
                <li className="flex justify-between"><span>Nominee</span><span className={c.nominee ? "text-emerald-600" : "text-red-500"}>{c.nominee ? "Registered" : "Missing"}</span></li>
                <li><span className="text-gray-400">Goals:</span> {c.goals?.join(", ")}</li>
                <li><span className="text-gray-400">Last transaction:</span> {c.last_txn}</li>
                <li><span className="text-gray-400">Last interaction:</span> {c.last_interaction}</li>
              </ul>
            </Card>
          </div>

          <Card>
            <h4 className="font-head font-semibold mb-3">Current holdings</h4>
            <div className="space-y-2">{c.holdings?.map((h) => (
              <div key={h.fund} className="flex items-center gap-3 text-sm border-b border-gray-100 pb-2 last:border-0">
                <div className="min-w-0"><div className="font-medium truncate">{h.fund}</div><div className="text-xs text-gray-400">{h.category} · {h.amc}</div></div>
                <div className="ml-auto font-semibold text-blue-600">{h.value}</div>
              </div>
            ))}</div>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card>
              <h4 className="font-head font-semibold mb-3">Communication history</h4>
              {c.interactions?.length ? <div className="space-y-2">{c.interactions.map((i, idx) => (
                <div key={`int-${idx}-${i.date || ""}`} className="text-sm flex gap-2"><Icon name="MessageSquare" size={14} className="text-gray-400 mt-0.5 shrink-0" /><div><b>{i.type}</b> · {i.date}<div className="text-xs text-gray-500">{i.note}</div></div></div>
              ))}</div> : <p className="text-sm text-gray-400">No interactions logged.</p>}
            </Card>
            <Card>
              <h4 className="font-head font-semibold mb-3">Redemption history</h4>
              {c.redemptions?.length ? <div className="space-y-2">{c.redemptions.map((r, idx) => (
                <div key={`red-${idx}-${r.fund || ""}`} className="text-sm flex justify-between"><span>{r.fund} · {r.date}</span><b className="text-red-500">{r.amount}</b></div>
              ))}</div> : <p className="text-sm text-gray-400">No redemptions.</p>}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
